import { Link } from "react-router-dom";
import { ArrowRight, ArrowUpRight } from "lucide-react";
import Seo from "@/components/Seo";
import Hero from "@/components/Hero";
import Atlas from "@/components/Atlas";
import Marquee from "@/components/Marquee";
import Reveal from "@/components/Reveal";
import StepsSection from "@/components/StepsSection";
import FaqAccordion from "@/components/FaqAccordion";
import ContactForm from "@/components/ContactForm";
import GlobeArt from "@/components/GlobeArt";
import { SOLUTIONS, GROUPS, CONTEXT_IMAGE, PANEL_IMAGES, HOME_FAQS, SITE } from "@/config/site";

function ChallengePanels() {
  const panels = [
    {
      key: "people",
      title: "Support your people and operations.",
      blurb: "Benefits that show up for employees, and staffing that keeps specialized work moving.",
      image: PANEL_IMAGES.people,
      alt: "Healthcare professionals reviewing information together on a tablet",
      links: SOLUTIONS.filter((s) => s.group === "People & Healthcare"),
    },
    {
      key: "costs",
      title: "Evaluate costs and efficiency.",
      blurb: "Look harder at tariffs, technology, and infrastructure spending — before renewals decide for you.",
      image: PANEL_IMAGES.costs,
      alt: "Container ship loaded with cargo at a commercial port",
      links: SOLUTIONS.filter((s) => s.group === "Business Efficiency"),
    },
    {
      key: "growth",
      title: "Explore growth and emerging opportunities.",
      blurb: "From AI-powered marketing to what comes next in enterprise strategy and emerging technology.",
      image: PANEL_IMAGES.growth,
      alt: "A team collaborating around laptops during a working session",
      links: SOLUTIONS.filter(
        (s) => s.group === "Marketing & Growth" || s.group === "Emerging Opportunities"
      ),
    },
  ];

  return (
    <section className="bg-platinum py-16 sm:py-24 lg:py-28" data-testid="challenge-panels">
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <Reveal>
          <h2 className="max-w-2xl font-display text-3xl font-bold tracking-tight text-ink sm:text-4xl lg:text-5xl">
            Different challenges. A clearer next step.
          </h2>
          <p className="mt-4 max-w-2xl text-lg leading-relaxed text-ink/75">
            Three broad starting points. Each leads to the destinations that fit it — no promises
            attached, just places to look.
          </p>
        </Reveal>
        <div className="mt-12 grid gap-6 md:grid-cols-3">
          {panels.map((p, i) => (
            <Reveal key={p.key} delay={i * 0.08}>
              <div className="group relative flex h-[440px] flex-col justify-end overflow-hidden rounded-md" data-testid={`challenge-panel-${p.key}`}>
                <img
                  src={p.image}
                  alt={p.alt}
                  loading="lazy"
                  className="absolute inset-0 h-full w-full object-cover transition-transform duration-700 group-hover:scale-105"
                />
                <div aria-hidden="true" className="absolute inset-0 bg-gradient-to-t from-midnight/95 via-midnight/40 to-midnight/5" />
                <div className="relative p-6 lg:p-7">
                  <h3 className="font-display text-xl font-bold leading-snug text-white">{p.title}</h3>
                  <p className="mt-2 text-sm leading-relaxed text-white/80">{p.blurb}</p>
                  <ul className="mt-4 space-y-2 border-t border-white/25 pt-4">
                    {p.links.map((s) => (
                      <li key={s.id}>
                        <Link
                          to={`/solutions/${s.slug}`}
                          data-testid={`challenge-panel-link-${s.slug}`}
                          className="group/link flex items-center gap-2 text-sm font-semibold text-white/90 transition-colors hover:text-white"
                        >
                          <ArrowRight className="h-3.5 w-3.5 text-white/60 transition-transform group-hover/link:translate-x-0.5" />
                          {s.name}
                        </Link>
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  );
}

function ContextSection() {
  return (
    <section className="bg-white py-16 sm:py-24 lg:py-28" data-testid="context-section">
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <div className="grid items-center gap-10 lg:grid-cols-12 lg:gap-14">
          <Reveal className="lg:col-span-6">
            <p className="font-display text-xs font-bold uppercase tracking-[0.25em] text-cobalt">
              Why DJC Global
            </p>
            <h2 className="mt-4 font-display text-3xl font-bold tracking-tight text-ink sm:text-4xl">
              A connection with context.
            </h2>
            <div className="mt-5 space-y-4 text-lg leading-relaxed text-ink/75">
              <p>
                The right solution is rarely the hard part — finding it is. DJC Global exists to
                know the landscape of specialized providers, and to put that knowledge in front of
                businesses that need one specific door opened.
              </p>
              <p className="text-base text-ink/70">
                We don't deliver the services ourselves. We help you identify a relevant solution,
                then connect you with the specialist who does — so you spend your time evaluating a
                good fit, not searching for one.
              </p>
            </div>
            <ul className="mt-7 space-y-3">
              {[
                { t: "Connect", d: "Match your challenge to the destination most likely to address it." },
                { t: "Context", d: "Explain what the provider does and what to ask before you talk." },
                { t: "No pressure", d: "You decide what to explore — and nothing is delivered by DJC directly." },
              ].map((item) => (
                <li key={item.t} className="flex gap-3 text-[15px]">
                  <span className="mt-0.5 shrink-0 whitespace-nowrap font-display text-sm font-bold text-cobalt">{item.t}</span>
                  <span className="text-ink/70">{item.d}</span>
                </li>
              ))}
            </ul>
            <div className="mt-8 flex flex-wrap items-center gap-4">
              <Link
                to="/our-approach"
                data-testid="context-approach-link"
                className="group inline-flex items-center gap-2 rounded-md border border-ink/20 bg-white px-6 py-3 font-display text-sm font-semibold text-ink transition-colors hover:border-cobalt hover:text-cobalt"
              >
                See Our Approach
                <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-0.5" />
              </Link>
              <a
                href={SITE.phoneHref}
                data-testid="context-phone-link"
                className="text-sm font-semibold text-ink transition-colors hover:text-cobalt"
              >
                Or call {SITE.phone}
              </a>
            </div>
          </Reveal>
          <Reveal delay={0.1} className="lg:col-span-6">
            <div className="relative">
              <img
                src={CONTEXT_IMAGE}
                alt="Sunlit glass atrium of a modern office building"
                loading="lazy"
                className="h-[320px] w-full rounded-md border border-silver/60 object-cover shadow-sm sm:h-[440px]"
                data-testid="context-image"
              />
              <div
                aria-hidden="true"
                className="absolute -bottom-5 -left-5 hidden h-32 w-32 rounded-md border-2 border-cobalt/30 sm:block"
              />
            </div>
          </Reveal>
        </div>
      </div>
    </section>
  );
}

function HomeFaq() {
  return (
    <section className="bg-white py-16 sm:py-24" data-testid="home-faq">
      <div className="mx-auto max-w-4xl px-4 sm:px-6 lg:px-8">
        <Reveal>
          <h2 className="font-display text-3xl font-bold tracking-tight text-ink sm:text-4xl">
            Practical questions, straight answers.
          </h2>
        </Reveal>
        <Reveal delay={0.08} className="mt-10">
          <FaqAccordion items={HOME_FAQS} testPrefix="home-faq" />
        </Reveal>
      </div>
    </section>
  );
}

function FinalContact() {
  return (
    <section className="relative overflow-hidden bg-midnight py-16 sm:py-24" data-testid="final-contact">
      <GlobeArt className="pointer-events-none absolute inset-x-0 bottom-0 h-[70%] w-full opacity-[0.18]" stroke="#C6CED8" stroke2="#0755C9" />
      <div className="relative mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <div className="grid items-start gap-10 lg:grid-cols-12 lg:gap-14">
          <Reveal className="lg:col-span-5">
            <p className="font-display text-xs font-bold uppercase tracking-[0.25em] text-silver">
              Let's Talk
            </p>
            <h2 className="mt-4 font-display text-3xl font-bold leading-tight tracking-tight text-white sm:text-4xl lg:text-5xl">
              Your next opportunity starts with a conversation.
            </h2>
            <p className="mt-5 max-w-md text-lg leading-relaxed text-white/75">
              Tell us what you'd like to improve. We'll help you find the destination — and the
              specialist — worth your time.
            </p>
            <div className="mt-8 space-y-2 text-sm text-white/85">
              <p>
                Prefer to call?{" "}
                <a href={SITE.phoneHref} data-testid="final-phone-link" className="font-semibold text-white underline decoration-silver/60 underline-offset-4 hover:decoration-white">
                  {SITE.phone}
                </a>
              </p>
              <p>
                Or email{" "}
                <a href={`mailto:${SITE.email}`} data-testid="final-email-link" className="font-semibold text-white underline decoration-silver/60 underline-offset-4 hover:decoration-white">
                  {SITE.email}
                </a>
              </p>
            </div>
            <p className="mt-8 max-w-md border-t border-white/20 pt-5 text-xs leading-relaxed text-white/55">
              DJC Global may receive compensation from certain referrals.{" "}
              <Link to="/referral-disclosure" data-testid="final-disclosure-link" className="underline hover:text-white">
                Referral disclosure
              </Link>
            </p>
          </Reveal>
          <Reveal delay={0.1} className="lg:col-span-7">
            <ContactForm compact />
          </Reveal>
        </div>
      </div>
    </section>
  );
}

export default function Home() {
  return (
    <>
      <Seo
        title="DJC Global — A World of Opportunity for Your Business"
        description={SITE.description}
        path="/"
        image="/djc-og.jpg"
      />
      <Hero />
      <Marquee />
      <Atlas />
      <ChallengePanels />
      <ContextSection />
      <StepsSection />
      <HomeFaq />
      <FinalContact />
    </>
  );
}
